import { gql } from '@apollo/client';
import { SalesAnalyticsData } from '@/components/locations/types';
import {
  TotalSales_GetSalesAnalyticsDataQuery,
  TotalSales_GetSalesAnalyticsDataQueryVariables,
} from '@/types';
import { useMemo, useState } from 'react';

import { BRONCO_SUPPLIER_UUID } from '@/mocks/uuids';
import { mockGetTotalSalesData } from '@/mocks/dsr-analytics/mock-graphql-data';
import { useWrappedQuery } from './use-wrapped-query';

export const GET_SALES_ANALYTICS_DATA_QUERY = gql`
  query TotalSales_GetSalesAnalyticsData(
    $chatUUID: uuid!
    $startTime: String!
    $endTime: String!
  ) {
    getSalesAnalyticsData(
      chat_id: $chatUUID
      start_time: $startTime
      end_time: $endTime
    ) {
      avg_order_size
      total_orders
      total_sales_data {
        date
        total_sales_amount_in_micros
      }
      total_sales_in_micros
      total_sales_percent_over_period
    }
  }
`;

interface QueryVariables {
  chatUUID: string;
  endTime: string;
  startTime: string;
}

export const useSalesAnalyticsData = (
  queryVariables: QueryVariables,
  supplierUUID: string,
) => {
  const shouldUseMockData = supplierUUID === BRONCO_SUPPLIER_UUID;
  const [data, setData] = useState<SalesAnalyticsData>();

  const {
    loading,
    data: fetchedData,
    error,
  } = useWrappedQuery<
    TotalSales_GetSalesAnalyticsDataQuery,
    TotalSales_GetSalesAnalyticsDataQueryVariables
  >(GET_SALES_ANALYTICS_DATA_QUERY, {
    context: {
      noBatch: true,
    },
    skip: shouldUseMockData,
    variables: queryVariables,
  });

  useMemo(async () => {
    let transformedData;
    if (shouldUseMockData) {
      const response = await mockGetTotalSalesData(queryVariables);
      const mockSalesData = response.data && response.data.salesData;

      if (mockSalesData) {
        transformedData = {
          avgOrderSize: mockSalesData.avgOrderSize,
          grossProfitInMicros: mockSalesData.grossProfitInMicros,
          grossProfitPercentOverPeriod:
            mockSalesData.grossProfitPercentOverPeriod,
          totalOrders: mockSalesData.totalOrders,
          totalSalesData: mockSalesData.totalSalesData.map(
            ({ date, orderAmountInMicros }) => ({
              date,
              totalSalesAmountInMicros: orderAmountInMicros,
            }),
          ),
          totalSalesInMicros: mockSalesData.totalSalesInMicros,
          totalSalesPercentOverPeriod:
            mockSalesData.totalSalesPercentOverPeriod,
        };
      }
    } else {
      const salesAnalyticsData = fetchedData?.getSalesAnalyticsData;
      if (salesAnalyticsData) {
        transformedData = {
          avgOrderSize: salesAnalyticsData.avg_order_size,
          grossProfitInMicros: null,
          grossProfitPercentOverPeriod: null,
          totalOrders: salesAnalyticsData.total_orders,
          totalSalesData: salesAnalyticsData.total_sales_data.map(
            ({ date, total_sales_amount_in_micros }) => ({
              date,
              totalSalesAmountInMicros: total_sales_amount_in_micros,
            }),
          ),
          totalSalesInMicros: salesAnalyticsData.total_sales_in_micros,
          totalSalesPercentOverPeriod:
            salesAnalyticsData.total_sales_percent_over_period,
        };
      }
    }
    setData(transformedData);
  }, [queryVariables, shouldUseMockData, fetchedData?.getSalesAnalyticsData]);

  return { data, error, loading };
};
